/**
 * SmartEventCreator (Web) — Instant event creation bar for Dashboard.
 * Collapsed: single input "What's your next event?". Expand to add date.
 * Uses Convex mutations to create events, react-router to navigate.
 */

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import {
  Plus,
  CalendarDays,
  ChevronRight,
  Loader2,
  Check,
  X,
} from 'lucide-react';

function getNextSaturday10pm(): string {
  const now = new Date();
  const day = now.getDay();
  let daysUntil = day === 6 ? 7 : 6 - day;
  if (daysUntil === 0) daysUntil = 7;
  const target = new Date(now);
  target.setDate(target.getDate() + daysUntil);
  target.setHours(22, 0, 0, 0);
  const yyyy = target.getFullYear();
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T22:00`;
}

type SmartEventCreatorProps = {
  onEventCreated?: (eventId: string) => void;
};

export function SmartEventCreator({ onEventCreated }: SmartEventCreatorProps) {
  const createEvent = useMutation(api.events.create);
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState('');
  const [dateTime, setDateTime] = useState(() => getNextSaturday10pm());
  const [isCreating, setIsCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<{ id: string; title: string; date: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCreate = useCallback(async () => {
    if (!title.trim()) return;
    setIsCreating(true);
    try {
      const eventDate = dateTime
        ? new Date(dateTime).toISOString()
        : new Date(getNextSaturday10pm()).toISOString();

      const eventId = await createEvent({
        title: title.trim(),
        date: eventDate,
        status: 'confirmed',
        visibility: 'private',
        lineup: [],
        tags: [],
      });

      setJustCreated({ id: eventId, title: title.trim(), date: eventDate });
      setTitle('');
      setDateTime(getNextSaturday10pm());
      setExpanded(false);

      setTimeout(() => setJustCreated(null), 4000);
      onEventCreated?.(eventId);
    } catch (err) {
      console.error('SmartEventCreator: Failed to create event', err);
    } finally {
      setIsCreating(false);
    }
  }, [title, dateTime, createEvent, onEventCreated]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && title.trim()) {
      e.preventDefault();
      handleCreate();
    }
    if (e.key === 'Escape') {
      setExpanded(false);
      inputRef.current?.blur();
    }
  };

  if (justCreated) {
    return (
      <div className="rounded-xl border border-[#25a48f]/30 bg-[#25a48f]/5 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#25a48f]/20 flex items-center justify-center">
            <Check className="w-4 h-4 text-[#25a48f]" />
          </div>
          <div>
            <p className="text-sm font-medium">"{justCreated.title}" created!</p>
            <p className="text-xs text-muted-foreground">
              {new Date(justCreated.date).toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
              })}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/app/event/${justCreated.id}`)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25a48f] text-white text-sm font-medium hover:bg-[#1e9480] transition-colors"
        >
          Open event
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border transition-all ${
        expanded
          ? 'border-[#25a48f]/40 bg-card shadow-lg shadow-[#25a48f]/5'
          : 'border-border bg-card hover:border-[#25a48f]/20'
      }`}
    >
      <div className="flex items-center gap-3 p-3">
        <div className="w-9 h-9 rounded-lg bg-[#25a48f]/10 flex items-center justify-center flex-shrink-0">
          <Plus className="w-4 h-4 text-[#25a48f]" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={() => setExpanded(true)}
          onKeyDown={handleKeyDown}
          placeholder="What's your next event?"
          className="flex-1 bg-transparent text-sm font-medium placeholder:text-muted-foreground/60 focus:outline-none"
        />
        {expanded && title.trim() && (
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#25a48f] text-white text-sm font-medium hover:bg-[#1e9480] transition-colors disabled:opacity-50"
          >
            {isCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
          </button>
        )}
        {expanded && (
          <button
            onClick={() => { setExpanded(false); setTitle(''); }}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-0 space-y-3 border-t border-border/50">
          <div className="pt-3 flex items-center gap-3">
            <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="datetime-local"
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              className="text-xs bg-transparent border border-input rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Press <kbd className="px-1 py-0.5 rounded bg-muted border border-border text-[10px] font-mono">Enter</kbd> to create instantly.
          </p>
        </div>
      )}
    </div>
  );
}
